import { forwardRef } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Container,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import { colors } from 'theme/mui/colors';
import Chip from 'components/v2/chip/Chip';
import { useLocalization } from 'localization';
import { createNewOrderStore } from 'stores/createNewOrderStore';
import { OrderItems, transformBasicPackage } from 'helpers/transformBasicPackage';
import useNewOrderCalculatePrice from 'hooks/general/useNewOrderCalculatePrice';
import useGetKeyPickUpFee from 'hooks/services/useGetKeyPickUpFee';
import { CurrencyCode } from 'constants/enums/general';
import { transformProducts } from 'helpers/transformProducts';
import { ProductStructure } from 'constants/enums/products';
import { userStore } from 'stores/userStore';
import hasBookPhotographerTask from 'helpers/hasBookPhotographerTask';

type Props = {};

const NewOrderReceipt = forwardRef<HTMLDivElement, Props>((_props, ref) => {
  const { t, f } = useLocalization();
  const { formData } = createNewOrderStore();
  const totalPrice = useNewOrderCalculatePrice();
  const keyPickUpFee = useGetKeyPickUpFee();
  const { user } = userStore();
  const showPrice = user?.visiblePrices;
  const currency = CurrencyCode.DKK;

  const isKeyPickupChosen =
    formData?.keyPickup && hasBookPhotographerTask() ? keyPickUpFee : { price: 0, name: '', quantity: 0 };

  const basicPackage = formData?.basicPackage?.name
    ? [
        {
          quantity: 1,
          name: formData?.basicPackage?.name ?? '',
          price: 0,
        },
      ]
    : [];

  const productsInsideBasicPackage = transformBasicPackage(formData?.basicPackage?.children ?? []);
  const bundleProductsPackage = transformProducts({
    item: formData?.bundlePackagesProduct ?? {},
    productStructure: ProductStructure.BundleProduct,
  });
  const singleProducts = transformProducts({
    item: formData?.singleProducts ?? {},
    productStructure: ProductStructure.SingleProduct,
  });

  const orderItems: OrderItems[] = [
    ...basicPackage,
    ...productsInsideBasicPackage,
    ...bundleProductsPackage,
    ...singleProducts,
    isKeyPickupChosen,
  ];

  return (
    <Container ref={ref} maxWidth="md" sx={{ py: 4 }}>
      <Card elevation={3} sx={{ overflow: 'hidden', borderRadius: 2 }}>
        <Stack direction="row" gap={2} alignItems="center" sx={{ px: 3, py: 4, backgroundColor: colors.blue.level10 }}>
          <CheckCircleOutlineIcon />
          <Typography variant="h4" component="h1" fontWeight="medium">
            {t('orders.pages.new.receipt.thanksTitle')}
          </Typography>
        </Stack>
        <Stack sx={{ pl: 4 }} alignItems="center" direction="row">
          <Typography variant="subtitle2">{`${t('common.terms.case')} :`}</Typography>
          <Chip label={formData?.case?.reference ?? '-'} />
        </Stack>
        <Stack sx={{ pl: 4 }} alignItems="center" direction="row">
          <Typography variant="subtitle2">{`${t('common.terms.address')} :`}</Typography>
          <Chip label={`${formData?.address?.full ?? '-'}`} />
        </Stack>
        <Stack sx={{ pl: 4 }} alignItems="center" direction="row">
          <Typography variant="subtitle2">Order ID:</Typography>
          <Chip label={`${formData?.orderId}`} />
        </Stack>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="body1">{t('orders.pages.new.receipt.thanksMessage')}</Typography>
          <Typography variant="h5" component="h2" fontWeight="medium" sx={{ mt: 4, mb: 3 }}>
            {t('common.terms.orderDetails')}
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell width="10%"> {t('common.terms.quantity')}</TableCell>
                  <TableCell>{t('cases.textTypes.description')}</TableCell>
                  {showPrice && <TableCell align="right">{t('common.terms.price')}</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {orderItems
                  .filter(item => item.name)
                  .map((item: OrderItems) => (
                    <TableRow key={item.name} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell>{item.quantity || 1}</TableCell>
                      <TableCell>{item.name ?? ''}</TableCell>
                      {showPrice && (
                        <TableCell align="right">
                          {item.price === 0 ? '' : `${f(item.price, { currency, decimals: 2 })}`}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                {showPrice && (
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell colSpan={2}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {t('common.terms.totalPrice')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="subtitle1" fontWeight="bold">
                        {totalPrice}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Container>
  );
});

export default NewOrderReceipt;
